import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
    Building2, 
    Plus, 
    Search,
    Loader2,
    AlertCircle,
    ChevronsRight,
    Receipt,
    XCircle,
    Download,
    Upload,
    FileText,
    Trash2
} from 'lucide-react';
import { boqApi } from '@/services/api';
import { Button } from "@/components/UI/button";
import { Input } from "@/components/UI/input";
import { 
    Card, 
    CardContent, 
    CardHeader, 
    CardTitle,
    CardDescription,
    CardFooter 
} from "@/components/UI/card";
import {
    Alert,
    AlertTitle,
    AlertDescription,
} from "@/components/UI/alert";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter
} from "@/components/UI/dialog";
import { Separator } from "@/components/UI/separator";
import { useToast } from "@/components/UI/use-toast";
import { cn } from '@/lib/utils';

// PDF Management Dialog Component
const PdfManagementDialog = ({ project, onPdfUpdate }) => {
    const { toast } = useToast();
    const [uploading, setUploading] = useState(false);

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.type !== 'application/pdf') {
            toast({
                title: "Error",
                description: "Please upload a PDF file",
                variant: "destructive"
            });
            return;
        }

        setUploading(true);
        try {
            await boqApi.uploadProjectPdf(project.id, file);
            onPdfUpdate();
            toast({
                title: "Success",
                description: "PDF uploaded successfully"
            });
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to upload PDF",
                variant: "destructive"
            });
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async () => {
        try {
            const response = await boqApi.downloadProjectPdf(project.id);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', project.pdf_filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to download PDF",
                variant: "destructive"
            });
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this PDF?')) return;
        
        try {
            await boqApi.deleteProjectPdf(project.id);
            onPdfUpdate();
            toast({
                title: "Success",
                description: "PDF deleted successfully"
            });
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to delete PDF",
                variant: "destructive"
            });
        }
    };

    return (
        <div className="space-y-4">
            <input
                type="file"
                id={`pdf-upload-${project.id}`}
                accept=".pdf"
                className="hidden"
                onChange={handleFileUpload}
            />
            
            <div className="flex gap-2">
                <Button
                    disabled={uploading}
                    onClick={() => document.getElementById(`pdf-upload-${project.id}`).click()}
                >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? 'Uploading...' : 'Upload PDF'}
                </Button>

                {project.pdf_filename && (
                    <>
                        <Button variant="outline" onClick={handleDownload}>
                            <Download className="h-4 w-4 mr-2" />
                            Download
                        </Button>
                        <Button variant="destructive" onClick={handleDelete}>
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                        </Button>
                    </>
                )}
            </div>

            {project.pdf_filename && (
                <div className="p-4 bg-secondary rounded">
                    <p className="text-sm font-medium">Current PDF:</p>
                    <p className="text-sm text-muted-foreground mt-1">
                        {project.pdf_filename}
                    </p>
                    {project.pdf_uploaded_at && (
                        <p className="text-xs text-muted-foreground mt-1">
                            Uploaded: {new Date(project.pdf_uploaded_at).toLocaleString()}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

// Add Bill Dialog Component
const AddBillDialog = ({ project, onBillAdded }) => {
    const [billData, setBillData] = useState({
        bill_number: '',
        description: '',
        amount: '',
        status: 'ACTIVE'
    });
    const [loading, setLoading] = useState(false);
    const { toast } = useToast();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await boqApi.createProjectBill(project.id, billData);
            onBillAdded(response.data);
            toast({
                title: "Success",
                description: "Bill added successfully"
            });
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to add bill",
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="text-sm font-medium">Bill Number</label>
                <Input
                    value={billData.bill_number}
                    onChange={(e) => setBillData({ ...billData, bill_number: e.target.value })}
                    placeholder="e.g., BILL NO. 1"
                    required
                />
            </div>
            <div>
                <label className="text-sm font-medium">Description</label>
                <Input
                    value={billData.description}
                    onChange={(e) => setBillData({ ...billData, description: e.target.value })}
                    placeholder="Enter bill description"
                    required
                />
            </div>
            <div>
                <label className="text-sm font-medium">Amount (AED)</label>
                <Input
                    type="number"
                    step="0.01"
                    value={billData.amount}
                    onChange={(e) => setBillData({ ...billData, amount: e.target.value })}
                    placeholder="0.00"
                    disabled={billData.status === 'EXCLUDED'}
                />
            </div>
            <div>
                <label className="text-sm font-medium">Status</label>
                <select
                    value={billData.status}
                    onChange={(e) => setBillData({ 
                        ...billData, 
                        status: e.target.value,
                        amount: e.target.value === 'EXCLUDED' ? '' : billData.amount 
                    })}
                    className="w-full mt-1 px-3 py-2 border rounded-md"
                >
                    <option value="ACTIVE">Active</option>
                    <option value="EXCLUDED">Excluded</option>
                </select>
            </div>
            <DialogFooter>
                <Button type="submit" disabled={loading}>
                    {loading ? 'Adding...' : 'Add Bill'}
                </Button>
            </DialogFooter>
        </form>
    );
};

// Bill Card Component
const BillCard = ({ bill }) => {
    const navigate = useNavigate();
    const isExcluded = bill.status === 'EXCLUDED';
    
    const handleManageItems = (e) => {
        e.stopPropagation();
        navigate(`/boq-dashboard/bills/${bill.id}/items`);
    };

    return (
        <Card 
            className={cn(
                "hover:shadow-sm transition-shadow relative overflow-hidden",
                isExcluded ? 'bg-gray-50' : 'cursor-pointer'
            )}
        >
            {isExcluded && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="transform rotate-45 text-gray-200 text-4xl font-bold whitespace-nowrap">
                        EXCLUDED
                    </div>
                </div>
            )}
            <CardContent className={cn(
                "p-4",
                isExcluded && "opacity-75"
            )}>
                <div className="space-y-2 relative z-10">
                    <div className="flex justify-between items-start">
                        <div>
                            <h3 className="font-semibold text-lg">
                                {bill.bill_number}
                            </h3>
                            <p className="text-sm text-gray-600">{bill.description}</p>
                        </div>
                        {isExcluded && (
                            <XCircle className="h-5 w-5 text-gray-400" />
                        )}
                    </div>
                    <div className="pt-2">
                        {isExcluded ? (
                            <p className="text-sm text-gray-500 font-medium">Excluded from total</p>
                        ) : (
                            <p className="text-lg font-bold text-primary">
                                AED {parseFloat(bill.amount || 0).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                })}
                            </p>
                        )}
                    </div>
                    {!isExcluded && (
                        <Button 
                            variant="ghost" 
                            className="w-full mt-2 text-sm hover:bg-primary/5"
                            onClick={handleManageItems}
                        >
                            Manage Items
                            <ChevronsRight className="ml-2 h-4 w-4" />
                        </Button>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

// Project Card Component
const ProjectCard = ({ project, onClick, onPdfUpdate }) => (
    <Card 
        className="hover:shadow-md transition-shadow cursor-pointer"
        onClick={onClick}
    >
        <CardContent className="p-4">
            <div className="flex justify-between items-start">
                <div>
                    <h3 className="font-semibold text-lg">{project.project_name}</h3>
                    <p className="text-sm text-green-600 mb-2">
                        {project.reference_number}
                    </p>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {project.description}
                    </p>
                </div>
                <Dialog>
                    <DialogTrigger asChild>
                        <Button 
                            variant="ghost" 
                            size="sm"
                            className="ml-2"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <FileText className="h-4 w-4" />
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Manage Project PDF</DialogTitle>
                        </DialogHeader>
                        <PdfManagementDialog 
                            project={project}
                            onPdfUpdate={onPdfUpdate}
                        />
                    </DialogContent>
                </Dialog>
            </div>
            
            <Separator className="my-3" />
            
            <div className="flex justify-between items-center">
                <div>
                    <span className="text-sm text-gray-500">Total Amount:</span>
                    <p className="font-semibold">
                        AED {parseFloat(project.total_amount || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        })}
                    </p>
                </div>
                <span className="text-sm text-primary">View Details →</span>
            </div>
        </CardContent>
    </Card>
);

// Main Dashboard Component
const BOQDashboard = () => {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const { toast } = useToast();

    useEffect(() => {
        fetchProjects();
    }, []);

    useEffect(() => {
        if (selectedProject) {
            fetchProjectBills(selectedProject.id);
        }
    }, [selectedProject]);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const response = await boqApi.getProjects();
            setProjects(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching projects:', err);
            setError(err.response?.data?.message || 'Failed to fetch projects');
            toast({
                title: "Error",
                description: "Failed to fetch projects",
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchProjectBills = async (projectId) => {
        try {
            const response = await boqApi.getProjectBills(projectId);
            setBills(response.data);
        } catch (err) {
            console.error('Error fetching bills:', err);
            toast({
                title: "Error",
                description: "Failed to fetch project bills",
                variant: "destructive"
            });
        }
    };

    const handleBillAdded = (newBill) => {
        setBills([...bills, newBill]);
    };

    const calculateTotalAmount = (bills) => {
        return bills
            .filter(bill => bill.status !== 'EXCLUDED')
            .reduce((sum, bill) => sum + parseFloat(bill.amount || 0), 0);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
                    <p className="text-sm text-muted-foreground">Loading projects...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <div className="bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="p-2 bg-primary rounded-lg">
                                <Building2 className="h-6 w-6 text-primary-foreground" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">BOQ Management</h1>
                                <p className="text-sm text-gray-500">Manage your Bill of Quantities</p>
                            </div>
                        </div>
                        {!selectedProject && (
                            <Button onClick={() => navigate('/boq-dashboard/new-project')}>
                                <Plus className="h-4 w-4 mr-2" />
                                New Project
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {error && (
                    <Alert variant="destructive" className="mb-6">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Error</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {selectedProject ? (
                    // Project Detail View
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <Button 
                                    variant="ghost" 
                                    onClick={() => setSelectedProject(null)}
                                    className="mb-2"
                                >
                                    ← Back to Projects
                                </Button>
                                <h2 className="text-2xl font-bold">{selectedProject.project_name}</h2>
                                <p className="text-sm text-green-600">
                                    {selectedProject.reference_number}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <Dialog>
                                    <DialogTrigger asChild>
                                        <Button>
                                            <Plus className="h-4 w-4 mr-2" />
                                            Add Bill
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent>
                                        <DialogHeader>
                                            <DialogTitle>Add New Bill</DialogTitle>
                                        </DialogHeader>
                                        <AddBillDialog 
                                            project={selectedProject}
                                            onBillAdded={handleBillAdded}
                                        />
                                    </DialogContent>
                                </Dialog>
                            </div>
                        </div>

                        {/* Bills Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {bills.map((bill) => (
                                <BillCard
                                    key={bill.id}
                                    bill={bill}
                                />
                            ))}
                        </div>

                        {/* Total Summary */}
                        {bills.length > 0 && (
                            <Card className="bg-primary/5">
                                <CardContent className="p-4">
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-lg font-semibold">Total Amount</h3>
                                        <p className="text-2xl font-bold">
                                            AED {calculateTotalAmount(bills).toLocaleString(undefined, {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {bills.length === 0 && (
                            <div className="text-center py-8 bg-white rounded-lg border">
                                <Receipt className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <h3 className="text-lg font-medium text-gray-900">No Bills Added</h3>
                                <p className="text-gray-500 mt-1 mb-4">
                                    Start by adding your first bill to this project
                                </p>
                            </div>
                        )}
                    </div>
                ) : (
                    // Projects List View
                    <>
                        <div className="mb-6">
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                                <Input
                                    placeholder="Search projects..."
                                    className="pl-10"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {projects
                                .filter(project => 
                                    project.project_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                    project.reference_number.toLowerCase().includes(searchQuery.toLowerCase())
                                )
                                .map((project) => (
                                    <ProjectCard
                                        key={project.id}
                                        project={project}
                                        onClick={() => setSelectedProject(project)}
                                        onPdfUpdate={fetchProjects}
                                    />
                                ))
                            }
                        </div>

                        {projects.length === 0 && (
                            <div className="text-center py-12">
                                <Receipt className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    No projects found
                                </h3>
                                <p className="text-gray-500">
                                    {searchQuery 
                                        ? "No projects match your search criteria"
                                        : "Start by creating your first BOQ project"}
                                </p>
                                {!searchQuery && (
                                    <Button 
                                        onClick={() => navigate('/boq-dashboard/new-project')}
                                        className="mt-4"
                                    >
                                        <Plus className="h-4 w-4 mr-2" />
                                        Create First Project
                                    </Button>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default BOQDashboard;
